/**
 * Jovii — IDENTITY, FRIENDS AND ROOM HISTORY, from the browser.
 *
 * A thin, typed mirror of `internal/social/api.ts`. Fourteen routes, no state, no
 * reactivity: `store.ts` owns everything that changes, and this file owns nothing but
 * the shape of a request. The wire contract is docs/SOCIAL.md § "HTTP surface" and it is
 * NOT redesigned here — where a field looks awkward it is because the server says so.
 *
 * ── THE ACCOUNT KEY, AND WHY IT IS SENT TWICE ──────────────────────────────────────
 *
 * The server mints 192 bits on the first `GET /api/me` and returns them ONCE, in that
 * one response body, and as an `HttpOnly` cookie. HttpOnly means this file cannot read
 * the cookie back, so it keeps its own copy in `localStorage` and re-presents it as
 * `X-Jove-Account` on every call.
 *
 * That is not belt-and-braces, it is two different failure modes:
 *  - the WebSocket upgrade carries cookies and cannot carry headers, so the cookie is
 *    the only thing that can identify a socket;
 *  - Safari's storage policy evicts cookies from sites nobody has visited for a week,
 *    and losing the cookie would silently turn one person into two — a new handle, an
 *    empty friends list, and the old account still holding every friendship.
 * With both, one visit repairs either. The server re-sets the cookie whenever a valid
 * key arrives by header.
 *
 * THE KEY IS A BEARER TOKEN. It is never put in a URL (unlike the invite code, which
 * deliberately has a `?invite=` form): URLs end up in screenshots, in history and in
 * referrers. It is never logged and never rendered.
 *
 * ── WHAT `401` MEANS HERE ──────────────────────────────────────────────────────────
 *
 * `GET /api/me` is the only route that mints. Every other route answers
 * `401 account-required` rather than quietly making a second person out of somebody
 * whose cookie expired mid-session — so the right response to that code is always
 * "call `me()` again", never "make a new account".
 */

// Relative, not `$lib/ui/api`, ON PURPOSE. `node --import tsx --test` runs against the
// ROOT tsconfig, which maps `$types` and nothing else, so a `$lib` value import here
// would make every unit test in this folder unrunnable outside Vite. `chat/store.ts`
// reaches for `../social/protocol` for the same reason.
import { ApiError, withRequestDeadline, type RoomSnapshot } from '../ui/api';
import type {
  AvatarId,
  FriendPresence,
  Handle,
  PublicProfile,
  RoomInvitation,
  SelfProfile,
  UserId
} from './protocol';

/** Where this browser keeps its copy of the account key. Versioned like every other
 *  key in the app, so a shape change is a new key rather than a migration. */
export const ACCOUNT_KEY_STORAGE = 'jt.account.v1';

/* ────────────────────────────────────────────────────────────────────────────
 * The key
 * ──────────────────────────────────────────────────────────────────────────── */

let cached: string | null | undefined;

/** This browser's account key, or null. Read through a cache so the hot path does not
 *  touch localStorage on every request. */
export function accountKey(): string | null {
  if (cached !== undefined) return cached;
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY_STORAGE);
    cached = raw !== null && raw !== '' ? raw : null;
  } catch {
    cached = null; // Safari private mode  // IOS-UNVERIFIED
  }
  return cached;
}

/** Remember the key the server just minted. Called from exactly one place: `me()`. */
export function rememberAccountKey(key: string): void {
  cached = key;
  try {
    localStorage.setItem(ACCOUNT_KEY_STORAGE, key);
  } catch {
    /* private mode: the key still holds for this page, and the cookie is doing the work */
  }
}

/**
 * Forget this browser's account.
 *
 * NOT a delete of the account: the handle, the friendships and the history are all still
 * on the server and come back if the key does. Destroying an account would take its
 * handle out of circulation for six months, which is not what a stray click should do.
 */
export function forgetAccountKey(): void {
  cached = null;
  try {
    localStorage.removeItem(ACCOUNT_KEY_STORAGE);
  } catch {
    /* nothing left to try */
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The request
 * ──────────────────────────────────────────────────────────────────────────── */

interface Options {
  method?: string;
  body?: unknown;
  signal?: AbortSignal | null;
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  return withRequestDeadline(signal => requestBody<T>(path, { ...options, signal }), options.signal,
    options.method !== undefined && options.method !== 'GET');
}

async function requestBody<T>(path: string, options: Options): Promise<T> {
  const key = accountKey();
  const headers: Record<string, string> = { accept: 'application/json' };
  if (key !== null) headers['x-jove-account'] = key;
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      headers,
      signal: options.signal ?? null,
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) })
    });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    throw new ApiError('Cannot reach the server.', 0);
  }

  const text = await response.text();
  let body: unknown = null;
  if (text !== '') {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const detail = body as { error?: string; message?: string; retryAfterMs?: number } | null;
    const error = new ApiError(
      detail?.message ?? `Request failed (${response.status})`,
      response.status,
      detail?.error
    );
    // `429` on a handle change carries how long is left. Carried through rather than
    // recomputed, so the screen can say the same number the server is enforcing.
    if (typeof detail?.retryAfterMs === 'number') (error as HandleError).retryAfterMs = detail.retryAfterMs;
    throw error;
  }
  return body as T;
}

/** An `ApiError` from `POST /api/me/handle` inside the cooldown carries the remainder. */
export interface HandleError extends ApiError {
  retryAfterMs?: number;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Identity
 * ──────────────────────────────────────────────────────────────────────────── */

export interface MeResponse {
  me: SelfProfile;
  /** Present exactly once, on the call that minted the account. */
  accountKey?: string;
  /** False means this instance forgets everybody on restart — see docs/SOCIAL.md
   *  § Deployment. Worth showing rather than hiding. */
  durable: boolean;
  serverTime: number;
}

/**
 * Who am I. **Mints an account on the first call**, which is why it is the only route
 * that may be called without one, and the only response that ever carries the raw key.
 */
let identityRequest: Promise<MeResponse> | null = null;
export function me(signal?: AbortSignal): Promise<MeResponse> {
  if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  // First-page callers share account creation and cookie repair. One aborted UI
  // subscriber must not abort identity initialization needed by the room socket.
  // Persist only after the deadline race resolves, never inside its fetch/body worker.
  identityRequest ??= request<MeResponse>('/api/me').then(body => {
    if (typeof body.accountKey === 'string' && body.accountKey !== '') rememberAccountKey(body.accountKey);
    return body;
  }).finally(() => { identityRequest = null; });
  if (!signal) return identityRequest;
  if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  const pending = identityRequest;
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    pending.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

export function setProfile(
  patch: { displayName?: string; avatar?: AvatarId; privateMode?: boolean },
  signal?: AbortSignal
): Promise<{ me: SelfProfile; durable: boolean }> {
  return request('/api/me', { method: 'POST', body: patch, signal });
}

/** Once a month, enforced on the server. Throws `ApiError` with `code: 'handle-cooldown'`
 *  and a `retryAfterMs` inside the window. */
export function setHandle(handle: string, signal?: AbortSignal): Promise<{ me: SelfProfile }> {
  return request('/api/me/handle', { method: 'POST', body: { handle }, signal });
}

export interface HandleCheck {
  available: boolean;
  /** The canonical form the server would store — not necessarily what was typed. */
  handle: Handle | null;
  /** `taken` | `held` | `reserved` | `bad-handle`, or null when it is free. */
  code: string | null;
  message: string | null;
}

export function checkHandle(handle: string, signal?: AbortSignal): Promise<HandleCheck> {
  return request(`/api/me/handle/check?handle=${encodeURIComponent(handle)}`, { signal });
}

/** Forget the cookie on this device. The account survives; see `forgetAccountKey`. */
export function forgetDevice(signal?: AbortSignal): Promise<{ forgotten: boolean }> {
  return request('/api/me', { method: 'DELETE', signal });
}

export function avatars(signal?: AbortSignal): Promise<{ avatars: AvatarId[] }> {
  return request('/api/avatars', { signal });
}

/** Authenticated, cancellable snapshot for invitation and room-entry checks. */
export async function invitationRoomSnapshot(id: string, signal?: AbortSignal): Promise<RoomSnapshot> {
  try { return await request(`/api/rooms/${encodeURIComponent(id)}`, { signal }); }
  catch (err) {
    if (err instanceof ApiError && err.status === 404)
      return { exists: false, roomId: id, ended: err.code === 'room-ended' };
    throw err;
  }
}

export function roomInvites(signal?: AbortSignal): Promise<{ invites: RoomInvitation[] }> {
  return request('/api/room-invites', { signal });
}

export function createRoomInvite(roomId: string, signal?: AbortSignal): Promise<{ token: string; roomId: string; expiresAt: number }> {
  return request('/api/room-invites', { method: 'POST', body: { roomId }, signal });
}

export function inviteFriend(roomId: string, to: UserId, signal?: AbortSignal): Promise<{ invite: RoomInvitation }> {
  return request('/api/room-invites', { method: 'POST', body: { roomId, to }, signal });
}

export function redeemRoomInvite(token: string, signal?: AbortSignal): Promise<{ roomId: string }> {
  return request('/api/room-invites/redeem', { method: 'POST', body: { token }, signal });
}

export function respondRoomInvite(id: string, accept: boolean, signal?: AbortSignal): Promise<{ accepted: boolean; roomId?: string }> {
  return request('/api/room-invites/respond', { method: 'POST', body: { id, accept }, signal });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Friends
 * ──────────────────────────────────────────────────────────────────────────── */

export interface IncomingRequest {
  id: string;
  from: PublicProfile;
  createdAt: number;
}

export interface OutgoingRequest {
  id: string;
  to: PublicProfile;
  createdAt: number;
}

export interface FriendsResponse {
  friends: PublicProfile[];
  incoming: IncomingRequest[];
  outgoing: OutgoingRequest[];
}

export function friends(signal?: AbortSignal): Promise<FriendsResponse> {
  return request('/api/friends', { signal });
}

/** By handle, prefixes before substrings, never yourself. An empty query is an empty
 *  list — the server does not enumerate accounts. */
export function searchFriends(q: string, signal?: AbortSignal): Promise<{ results: PublicProfile[] }> {
  return request(`/api/friends/search?q=${encodeURIComponent(q)}`, { signal });
}

/** What each friend is watching. The REST twin of the `friendPresence` push, and the
 *  only one of the two that works without the room socket. */
export function presence(signal?: AbortSignal): Promise<{ friends: FriendPresence[]; serverTime: number }> {
  return request('/api/friends/presence', { signal });
}

export function requestFriend(
  target: { handle: string } | { userId: UserId },
  signal?: AbortSignal
): Promise<{ requestId: string; accepted: boolean; to: PublicProfile }> {
  return request('/api/friends/request', { method: 'POST', body: target, signal });
}

export function respondFriend(
  requestId: string,
  accept: boolean,
  signal?: AbortSignal
): Promise<{ state: string; friends: PublicProfile[] }> {
  return request('/api/friends/respond', { method: 'POST', body: { requestId, accept }, signal });
}

export function cancelRequest(requestId: string, signal?: AbortSignal): Promise<{ state: string }> {
  return request('/api/friends/cancel', { method: 'POST', body: { requestId }, signal });
}

export function removeFriend(userId: UserId, signal?: AbortSignal): Promise<{ friends: PublicProfile[] }> {
  return request('/api/friends/remove', { method: 'POST', body: { userId }, signal });
}

/** Room history endpoints are retired. The browser writer is also disabled;
 * old local bytes remain untouched. Do not restore server transcripts. */
